import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ListChecks, CalendarClock, RotateCcw, Send, Trash2, Check, AlertTriangle, Flag,
} from "lucide-react";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const ITEM_PILL = {
  waiting: ["bg-sand-200 text-ink/65", "Waiting for their next visit"],
  pending_confirm: ["bg-amber-50 text-amber-800", "Needs your OK"],
  on_visit: ["bg-indigo-50 text-indigo-700", "On today's list"],
  done: ["bg-emerald-50 text-emerald-700", "Done"],
  cant: ["bg-orange-50 text-orange-700", "They can't do it"],
  bounced: ["bg-red-50 text-red-700", "Not their scope"],
  removed: ["bg-sand-200 text-ink/40", "Removed"],
};

export default function VisitQueue({ tenantId, readOnly }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("live");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/tenants/${tenantId}/field-services/queue`, { params: { status: filter } });
    setD(data);
  }, [tenantId, filter]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load the queue")); }, [load]);

  if (!d) return <div className="grid h-48 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  const s = d.stats || {};

  const op = async (id, o, body, msg) => {
    setBusy(id);
    try { await api.post(`/tenants/${tenantId}/field-services/queue/${id}/${o}`, body || {}); if (msg) toast.success(msg); await load(); }
    catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : det?.message || "Try again");
    }
    setBusy("");
  };

  return (
    <div className="space-y-5" data-testid="fs-visit-queue">
      <p className="rounded-xl bg-sand-100 px-4 py-3 text-xs leading-relaxed text-ink/65">
        A bag of trash reported on a Sunday, when the trash company is already coming Tuesday, shouldn't
        become its own dispatch — and it shouldn't be forgotten either. It joins their next contracted
        visit. The {V.lc("person")} is told it's on the list, and told again when it's done. Anything not ticked
        off rolls forward and starts counting.
      </p>

      <div className="grid gap-2 sm:grid-cols-4">
        {[["Waiting", s.waiting || 0, "bg-white"], ["On today's visits", s.on_visit || 0, "bg-white"],
          ["Rolled over", s.rolled || 0, (s.rolled ? "bg-amber-50" : "bg-white")],
          ["Needs your OK", s.pending_confirm || 0, (s.pending_confirm ? "bg-amber-50" : "bg-white")]].map(([l, v, bg]) => (
          <div key={l} className={`rounded-2xl border border-sand-200 px-4 py-3 ${bg}`} data-testid={`queue-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-2xl font-extrabold text-ink">{v}</div>
            <div className="text-xs font-semibold text-ink/50">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {[["live", "Open"], ["done", "Done"], ["cant,bounced", "Bounced back"], ["", "Everything"]].map(([k, label]) => (
          <button key={k || "all"} data-testid={`queue-filter-${k || "all"}`} onClick={() => setFilter(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${filter === k
              ? "bg-ink text-white" : "bg-sand-100 text-ink/55 hover:bg-sand-200"}`}>{label}</button>
        ))}
      </div>

      {!(d.vendors || []).length ? (
        <div data-testid="queue-empty" className="rounded-2xl border border-dashed border-sand-300 py-12 text-center">
          <ListChecks className="mx-auto h-7 w-7 text-ink/20" />
          <p className="mt-2 text-sm text-ink/50">Nothing on anybody's list.</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink/40">
            When {V.a("person")} reports something that a company already comes here for, it lands here
            automatically — or add one from a request on the Requests board.
          </p>
        </div>
      ) : (d.vendors || []).map((g) => (
        <div key={g.vendor_id} data-testid={`queue-vendor-${g.vendor_id}`} className="rounded-2xl border border-sand-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sand-100 px-4 py-3">
            <div className="font-heading text-base font-bold text-ink">{g.vendor_name}</div>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink/50">
              <CalendarClock className="h-3.5 w-3.5" />
              {g.next_visit ? `Next visit ${g.next_visit}${g.day ? ` (${g.day})` : ""}` : "no recurring visits set up"}
              <span className="ml-1 rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-bold uppercase text-ink/55">
                {g.items.length} item(s)
              </span>
            </div>
          </div>
          <div className="divide-y divide-sand-100">
            {g.items.map((it) => {
              const [cls, label] = ITEM_PILL[it.status] || ["bg-sand-200 text-ink/55", it.status];
              return (
                <div key={it.id} data-testid={`queue-item-${it.id}`} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                  <div className="min-w-[12rem] flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
                      {it.ref && <span className="font-mono text-[11px] font-bold text-ink/35">{it.ref}</span>}
                      {(it.rolled_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700">
                          <RotateCcw className="h-3 w-3" /> rolled {it.rolled_count}×
                        </span>
                      )}
                      {it.reports > 1 && <span className="text-[11px] font-bold text-ink/45">{it.reports} {V.lcs("person")} reported it</span>}
                      {it.resident_flagged && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-redline-600">
                          <Flag className="h-3 w-3" /> {V.lc("person")} says wrong vendor
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-ink">{it.summary}</div>
                    <div className="text-xs text-ink/45">
                      {CAT_LABEL[it.category] || it.category}{it.location ? ` · ${it.location}` : ""}
                      {it.cant_reason ? ` · "${it.cant_reason}"` : ""}
                      {it.bounce_note ? ` · "${it.bounce_note}"` : ""}
                    </div>
                    {(it.photo_urls || []).length > 0 && (
                      <div className="mt-2 flex gap-1.5">
                        {it.photo_urls.map((u, i) => <img key={i} src={u} alt="" className="h-12 w-12 rounded-lg object-cover" />)}
                      </div>
                    )}
                    {it.proof_url && (
                      <div className="mt-2 flex items-center gap-2">
                        <img src={it.proof_url} alt="proof" className="h-16 w-16 rounded-lg object-cover" />
                        <span className="text-xs font-semibold text-emerald-700">proof from the crew</span>
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="flex shrink-0 flex-wrap gap-1.5">
                      {it.status === "pending_confirm" && (
                        <button data-testid={`queue-confirm-${it.id}`} disabled={busy === it.id}
                          onClick={() => op(it.id, "confirm", {}, `On the list — the ${V.lc("person")} was told`)}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">
                          <Check className="h-3.5 w-3.5" /> Add it
                        </button>
                      )}
                      {["waiting", "on_visit", "pending_confirm", "cant", "bounced"].includes(it.status) && (
                        <button data-testid={`queue-dispatch-${it.id}`} disabled={busy === it.id}
                          onClick={() => op(it.id, "dispatch-now", {}, "Sent as its own job")}
                          className="inline-flex items-center gap-1 rounded-lg border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/65 disabled:opacity-40">
                          <Send className="h-3.5 w-3.5" /> Send now
                        </button>
                      )}
                      {["waiting", "on_visit", "pending_confirm"].includes(it.status) && (
                        <button data-testid={`queue-remove-${it.id}`} disabled={busy === it.id}
                          onClick={() => op(it.id, "remove", { reason: "removed by the manager" }, "Taken off the list")}
                          className="rounded-lg p-1.5 text-ink/30 hover:bg-redline-50 hover:text-redline-600 disabled:opacity-40">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {(s.bounced || s.cant) > 0 && filter === "live" && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-xs leading-relaxed text-amber-900">
            {(s.cant || 0) + (s.bounced || 0)} item(s) came back from a crew as "can't do it" or "not ours".
            <button onClick={() => setFilter("cant,bounced")} className="ml-1 font-bold underline">See them</button>
          </div>
        </div>
      )}
    </div>
  );
}
